import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { CompanySettings } from '@/types/settings';
import { useHasPermission } from '@/hooks/usePermissions';

interface UseCompanySettingsOptions {
  includeLogoDataUri?: boolean;
  enabled?: boolean;
}

/**
 * @param options.includeLogoDataUri Request the inline `logoDataUri` for PDF
 *   rendering. Off by default — only the estimate-PDF flow needs it, and it
 *   costs an extra server-side S3 GetObject + base64 (NOT-236). When enabled,
 *   a distinct query key keeps that payload out of the on-screen
 *   company-settings cache (which only uses `logoUrl`).
 */
export function useCompanySettings(options: UseCompanySettingsOptions = {}) {
  const canViewCompany = useHasPermission('company:view');
  const includeLogoDataUri = options?.includeLogoDataUri ?? false;
  const query = useApiQuery<CompanySettings>({
    queryKey: includeLogoDataUri ? settingsKeys.companyWithLogoDataUri() : settingsKeys.company(),
    queryFn: () =>
      apiGet<CompanySettings>(
        operationsClient,
        settingsEndpoints.company(),
        includeLogoDataUri ? { params: { includeLogoDataUri: true } } : undefined,
      ),
    enabled: canViewCompany && (options.enabled ?? true),
    showErrorToast: false,
  });
  return canViewCompany ? query : { ...query, data: undefined };
}
